import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTriangleExclamation, faCircleCheck, faSpinner } from '@fortawesome/free-solid-svg-icons'
import type { SystemHealthSummary } from '../../../types/systemHealth'
import { deriveHealthLevel, healthLevelClasses, healthLevelLabel } from '../../../utils/systemHealthStatus'
import { UNPROCESSED_WARNING } from '../../../constants/systemHealth'
import { cn } from '../../../shared/utils/cn'

interface HealthBannerProps {
  summary: SystemHealthSummary | undefined
  isLoading: boolean
}

// Terminally-failed messages deliberately do NOT appear here (Rejected (1h)
// is the exception that proves the rule: it is messages being turned away
// right now, which IS live state). This banner reports live pipeline state — what arrived and got processed — and turns
// green when that is healthy. A count of past permanent failures says nothing
// about whether the pipeline is working right now, and putting it inside a
// green "Healthy" box states two contradictory things at once. It lives in its
// own card below instead: see FailedMessagesSection.
export function HealthBanner({ summary, isLoading }: HealthBannerProps) {
  const level = deriveHealthLevel(summary)
  const classes = healthLevelClasses(level)
  const label = healthLevelLabel(level)
  const icon = level === 'green' ? faCircleCheck : faTriangleExclamation

  return (
    <div className={cn('rounded-lg border p-6', classes.bg, classes.border)}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-4">
          <FontAwesomeIcon
            icon={isLoading ? faSpinner : icon}
            className={cn('h-8 w-8', classes.text, isLoading && 'animate-spin')}
          />
          <div>
            <p className={cn('text-2xl font-semibold', classes.text)}>{isLoading ? 'Checking…' : label}</p>
            <p className="text-sm text-gray-600">
              {summary?.healthcheckError
                ? `Healthcheck error: ${summary.healthcheckError}`
                : summary
                ? `Reported status: ${summary.status}`
                : 'Awaiting status…'}
            </p>
          </div>
        </div>

        <dl className={cn('grid gap-6', summary?.rejectedLastHour !== undefined ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-3')}>
          <Metric label="Queue depth" value={summary?.queue.depth} />
          <Metric label="Poison" value={summary?.queue.poison} highlight={(summary?.queue.poison ?? 0) > 0} />
          <Metric label="Unprocessed" value={summary?.unprocessed} highlight={(summary?.unprocessed ?? 0) >= UNPROCESSED_WARNING} />
          {/* Only once the API reports it: a dash here would read as "none". */}
          {summary?.rejectedLastHour !== undefined && (
            <Metric
              label="Rejected (1h)"
              value={summary.rejectedLastHour}
              highlight={summary.rejectedLastHour > 0}
            />
          )}
        </dl>
      </div>
    </div>
  )
}

function Metric({
  label,
  value,
  highlight,
}: {
  label: string
  value: number | undefined
  highlight?: boolean
}) {
  return (
    <div className="text-right">
      <dt className="text-xs uppercase tracking-wide text-gray-500">{label}</dt>
      <dd
        className={cn(
          'text-2xl font-semibold',
          highlight ? 'text-red-700' : 'text-gray-900'
        )}
      >
        {value ?? '—'}
      </dd>
    </div>
  )
}
